import { objUtils } from '../../../utilities/formatting/object.utils';

export interface ISmartEnum {
    readonly name: string;
}

type StaticSmartEnum<TEnum> = {
    new (...args: any[]): TEnum;
    getNames(): string[];
    fromName(name: string): TEnum | undefined;
    fromNames(names: string[]): Set<TEnum>;
};

export abstract class SmartEnum<TEnum extends SmartEnum<TEnum>> {
    private static readonly enumRegistry: Map<string, Map<string, any>> = new Map();

    public readonly name: string;

    protected constructor(name: string) {
        this.name = name;
        let enumType = objUtils.getParentConstructorBeforeConstructorName(this.constructor, 'StatusEnum');

        if (!SmartEnum.enumRegistry.has(enumType)) {
            SmartEnum.enumRegistry.set(enumType, new Map<string, TEnum>());
        }

        SmartEnum.enumRegistry.get(enumType)!.set(name.toLowerCase(), this);
    }

    // Retrieve all names for the specific derived class
    public static getNames<TEnum extends SmartEnum<TEnum>>(this: StaticSmartEnum<TEnum>): string[] {
        const map = SmartEnum.enumRegistry.get(this.name);
        return map ? Array.from(map.values()).map(e => e.name) : [];
    }

    // Retrieve an instance by name (case-insensitive)
    public static fromName<TEnum extends SmartEnum<TEnum>>(this: StaticSmartEnum<TEnum>, name: string): TEnum | undefined {
        const map = SmartEnum.enumRegistry.get(this.name);
        return map?.get(name.toLowerCase());
    }

    // Retrieve a set of instances by a list of names
    public static fromNames<TEnum extends SmartEnum<TEnum>>(this: StaticSmartEnum<TEnum>, names: string[]): Set<TEnum> {
        const results = new Set<TEnum>();
        names.forEach(name => {
            const instance = this.fromName(name);
            if (instance) {
                results.add(instance);
            }
        });
        return results;
    }
}
